import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const HistoryContext = createContext();

export const HistoryProvider = ({ children }) => {
  const location = useLocation();
  const [historyStack, setHistoryStack] = useState(() => {
    return JSON.parse(sessionStorage.getItem("historyStack")) || [];
  });

  useEffect(() => {
    setHistoryStack((prevStack) => {
      if (!prevStack.length || prevStack[prevStack.length - 1].path !== location.pathname) {
        const newEntry = { path: location.pathname, title: document.title };
        const newStack = [...prevStack, newEntry];
        sessionStorage.setItem("historyStack", JSON.stringify(newStack));
        return newStack;
      }
      return prevStack;
    });
  }, [location]);

  /**
   * Get a history entry based on step, path, and match type.
   * @param {number} step - Position from current (e.g., -3 for 3rd back, +2 for 2nd forward).
   * @param {string} path - Path to search for.
   * @param {"exact" | "partial"} matchType - Match type (default: "exact").
   * @returns {{ path: string, title: string } | null} - Found entry or null.
   */
  const getHistoryEntry = (step, path, matchType = "exact") => {
    if (!historyStack.length) return null;

    const currentIndex = historyStack.findIndex(entry => entry.path === location.pathname);
    if (currentIndex === -1) return null;

    const isBackward = step < 0;
    const targetMatchIndex = Math.abs(step); // Extracts the match number (e.g., 3 from -3)
    
    const searchStack = isBackward 
      ? historyStack.slice(0, currentIndex).reverse() // Backward search
      : historyStack.slice(currentIndex + 1); // Forward search

    let matchCount = 0;
    for (const entry of searchStack) {
      const isMatch = matchType === "exact"
        ? entry.path === path
        : entry.path.includes(path);

      if (isMatch) {
        matchCount++;
        if (matchCount === targetMatchIndex) {
          return entry; // Return the nth match in the search direction
        }
      }
    }

    return null; // No match found
  };

  return (
    <HistoryContext.Provider value={{ historyStack, getHistoryEntry }}>
      {children}
    </HistoryContext.Provider>
  );
};

export const useHistoryStore = () => useContext(HistoryContext);
